"use client";
import { useMemo } from "react";
import { Avatar, Typography } from "antd/lib";
import { useTranslations } from "next-intl";
import useAxiosQuery from "@/hooks/useMyQuery";
import type { MyColumnType } from "@/app/components/MyTable";
import MyTable from "@/app/components/MyTable";
import MyCard from "@/app/components/MyCard";
import Heading from "@/app/components/Heading";
import type {
  IPositiveNegativeCorrelation,
  IPriceCorrelation,
  IPriceRelatedForecast,
} from "@/app/components/products/ProductForecast/index";
import { formatThousands } from "@/helpers";

interface InsightItemIF {
  coefficient?: number;
  price?: number;
  name: string;
  symbol: string;
  logo_uri: string;
}

const CardWithTable = ({
  title,
  path,
  titleLastColumn,
  symbol,
}: {
  title: string;
  path: string;
  titleLastColumn?: string;
  symbol: string;
}) => {
  const t = useTranslations("products");

  const { data: responseCorrelation } = useAxiosQuery<{ data?: IPriceCorrelation }>({
    url: "v1/product/forecast/correlation",
    params: {
      symbol,
    },
  });

  const { data: responseRelatedForecast } = useAxiosQuery<{ data?: IPriceRelatedForecast[] }>({
    url: "v1/product/forecast/related",
    params: { symbol },
  });

  const combinedData: {
    [key: string]: IPriceRelatedForecast[] | IPositiveNegativeCorrelation[];
  } | null = useMemo(
    () =>
      responseCorrelation?.data && responseRelatedForecast?.data
        ? {
            related: responseRelatedForecast.data,
            positive: responseCorrelation.data.positive,
            negative: responseCorrelation.data.negative,
          }
        : null,
    [responseRelatedForecast, responseCorrelation],
  );

  const data = useMemo(
    () => (combinedData ? (combinedData[path] as IPriceRelatedForecast[] | IPositiveNegativeCorrelation[]) : undefined),
    [path, combinedData],
  );

  const columns = useMemo<MyColumnType<InsightItemIF>[]>(
    () => [
      {
        title: t("Name"),
        key: "symbol",
        dataIndex: "symbol",
        width: 300,
        className: "cursor-pointer border-none py-1 min-w-[150px]",
        render: (_, record) => (
          <div className="flex gap-1 items-center">
            <Avatar src={record.logo_uri} />
            <div className="flex flex-col gap-1">
              <Typography.Text strong>{record.name}</Typography.Text>
              <Typography.Text type={"secondary"}>{record.symbol}</Typography.Text>
            </div>
          </div>
        ),
      },
      {
        title: titleLastColumn || t("Coefficient"),
        key: "change",
        width: 100,
        dataIndex: "change",
        className: "cursor-pointer border-none py-1",
        render: (_, record) => formatThousands(titleLastColumn ? record.price : record.coefficient),
      },
    ],
    [titleLastColumn, t],
  );
  return (
    <MyCard className={"h-full"} title={<Heading title={title} icon={null} />}>
      <MyTable
        className="w-full"
        dataSource={data?.map(i => ({ ...i, key: i.symbol || i.name })) ?? []}
        columns={columns}
        scroll={{ x: 500 }}
      />
    </MyCard>
  );
};
const ForecastContent = ({ symbol }: { symbol: string }) => {
  const t = useTranslations("products");
  const listCard = useMemo(
    () => [
      {
        title: t("Positive_Correlation"),
        path: "positive",
      },
      {
        title: t("Negative_Correlation"),
        path: "negative",
      },
      {
        title: t("1D_Related_Forecast"),
        path: "related",
        titleLastColumn: t("Price"),
      },
    ],
    [t],
  );
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
      {listCard.map((item, index) => (
        <CardWithTable
          key={index}
          titleLastColumn={item.titleLastColumn}
          title={item.title}
          path={item.path}
          symbol={symbol}
        />
      ))}
    </div>
  );
};

export default ForecastContent;
